// Dashboard QUICK FILTER chrome — the toggle + the applied-filter bar.
//
// Shared by every surface that renders a dashboard (designer preview / Live
// Preview pane, smart-page D embed, runtime client) so the mode looks and
// behaves identically everywhere. The state itself is the host's: it owns the
// `QuickFilterChip[]` and re-fetches its tiles whenever they change.
//
// The design point: quick filter NEVER applies silently. Turning the mode on
// paints an explicit banner explaining the gesture, every applied filter is a
// named chip with its own ✕, and the bar states how many tiles the filters
// actually reached (tiles that could not honour one say so on the tile).

import { useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { chipCaption } from './quickFilter'
import type { QuickFilterChip, TileFilterIndication } from './quickFilter'

const ACCENT = 'var(--pg-primary, #3b82f6)'
const WARN = 'var(--pg-warning, #d97706)'
const WARN_TEXT = 'var(--pg-warning, #b45309)'

/**
 * A translucent accent wash that stays OPAQUE — and therefore readable.
 *
 * `--pg-primary-soft` was referenced here but is defined by no host stylesheet,
 * so it always fell through to a bare 10%-alpha colour composited over whatever
 * sat behind the element. Over the dashboard grid that reads as see-through, and
 * chart ink shows through the bar's own chip text.
 *
 * The wash is a one-stop gradient painted over an opaque `--color-bg`: the same
 * tinted look, on a solid surface, in both themes. Two LONGHANDS rather than the
 * `background` shorthand on purpose — a shorthand containing `var()` is resolved
 * at computed-value time, and anything invalid there drops the whole property
 * back to transparent, which is exactly the bug being fixed.
 */
function softAccent(alpha = 0.10): CSSProperties {
  const wash = `rgba(59,130,246,${alpha})`
  return {
    backgroundColor: 'var(--color-bg, #fff)',
    backgroundImage: `linear-gradient(${wash}, ${wash})`,
  }
}

/** The multi-select modifier key, named for the platform (⌘ on Mac, Ctrl else).
 *  Guarded so a non-browser render (tests/SSR) never throws on `navigator`. */
function modifierKeyName(): string {
  try {
    if (typeof navigator === 'undefined') return 'Ctrl'
    const ua = `${navigator.platform || ''} ${navigator.userAgent || ''}`.toLowerCase()
    return ua.includes('mac') ? '⌘' : 'Ctrl'
  } catch {
    return 'Ctrl'
  }
}

/** Inline funnel glyph (the package carries no icon dependency). */
export function FunnelIcon({ size = 13, slashed = false }: { size?: number; slashed?: boolean }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }} aria-hidden="true"
    >
      <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
      {slashed && <path d="M2 2l20 20" />}
    </svg>
  )
}

/** Tick glyph — the "this tile did honour the filter" mark. */
function CheckIcon({ size = 9 }: { size?: number }) {
  return (
    <svg
      width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }} aria-hidden="true"
    >
      <path d="M20 6L9 17l-5-5" />
    </svg>
  )
}

export interface TileFilterBadgeProps {
  /** This tile's filter outcome (null = no filter active → nothing to say). */
  indication: TileFilterIndication | null
  /**
   * Why this tile is queried live instead of folded from the shared model
   * (MODEL_FALLBACK_REASON_TEXT[plan.reason]). Shown as its own pill and, when a
   * filter could not reach the tile, as the underlying cause in the popover —
   * "raw-SQL visual, queried on its own" is usually the REAL answer to "why
   * wasn't this filtered", and the two facts are far more useful together.
   */
  liveReason?: string | null
  compact?: boolean
}

/**
 * The per-tile filter indicator, shared by every dashboard surface.
 *
 * Marks the tile whether or not the filter reached it. An unmarked tile is
 * ambiguous — the user cannot tell "this number already respects your filter"
 * from "this number ignored it" — and that ambiguity is precisely what makes a
 * partially-filterable dashboard untrustworthy. Hovering either pill opens the
 * explanation: which chips were applied, which were not, and why.
 *
 * The popover is `position: fixed` because dashboard tiles clip their overflow;
 * an absolutely-positioned panel would be sliced off at the tile edge.
 */
export function TileFilterBadge({ indication, liveReason, compact = false }: TileFilterBadgeProps) {
  const [at, setAt] = useState<{ top: number; left: number } | null>(null)
  const ref = useRef<HTMLSpanElement | null>(null)
  if (!indication && !liveReason) return null

  const open = () => {
    const r = ref.current?.getBoundingClientRect()
    if (r) setAt({ top: r.bottom + 6, left: r.left })
  }
  const close = () => setAt(null)

  const good = indication?.state === 'filtered'
  const tone = !indication ? 'muted' : good ? 'ok' : 'warn'
  const color = tone === 'ok' ? ACCENT : tone === 'warn' ? WARN_TEXT : 'var(--color-text-secondary, #6b7280)'
  const border = tone === 'ok' ? ACCENT : tone === 'warn' ? WARN : 'var(--color-border, #e5e7eb)'

  const pill: CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0,
    fontSize: compact ? 9 : 10, fontWeight: 600, padding: '0 5px', borderRadius: 9,
    background: 'var(--color-bg, #fff)', whiteSpace: 'nowrap',
  }

  return (
    <span
      ref={ref}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
      tabIndex={0}
      // The one-line summary also lands on `title`: a touch device has no hover,
      // and a screen reader gets the same sentence the popover shows.
      title={[indication?.text, liveReason ? `Queried live: ${liveReason}.` : '']
        .filter(Boolean).join(' ')}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'help', outline: 'none' }}
    >
      {indication && (
        <span style={{ ...pill, color, border: `1px solid ${border}` }}>
          {good ? <CheckIcon size={compact ? 8 : 9} /> : <FunnelIcon size={compact ? 8 : 9} slashed={indication.state === 'none'} />}
          {indication.label}
        </span>
      )}
      {liveReason && (
        <span style={{
          ...pill, fontWeight: 500,
          color: 'var(--color-text-secondary, #6b7280)',
          border: '1px solid var(--color-border, #e5e7eb)',
        }}>
          live query
        </span>
      )}
      {at && (
        <span
          role="tooltip"
          style={{
            position: 'fixed', top: at.top, left: at.left, zIndex: 10070,
            maxWidth: 300, padding: '7px 9px', borderRadius: 6,
            border: '1px solid var(--color-border, #e5e7eb)',
            background: 'var(--color-bg, #fff)', color: 'var(--color-text, #111)',
            boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
            fontSize: 11, fontWeight: 400, lineHeight: 1.45, whiteSpace: 'normal',
            display: 'block', textAlign: 'left', pointerEvents: 'none',
          }}
        >
          {indication?.applied.length ? (
            <span style={{ display: 'block', color: ACCENT, fontWeight: 600 }}>
              Filtered by {indication.applied.join(', ')}
            </span>
          ) : null}
          {indication?.skipped.map((s) => (
            <span key={s.label} style={{ display: 'block', marginTop: 3 }}>
              <span style={{ fontWeight: 600, color: WARN_TEXT }}>Not filtered by {s.label}</span>
              {' — '}{s.why}.
            </span>
          ))}
          {liveReason && (
            <span style={{
              display: 'block', marginTop: indication ? 5 : 0,
              paddingTop: indication ? 5 : 0,
              borderTop: indication ? '1px solid var(--color-border, #e5e7eb)' : undefined,
              color: 'var(--color-text-secondary, #6b7280)',
            }}>
              This tile is queried on its own rather than folded from the shared
              dataset: {liveReason}.
            </span>
          )}
        </span>
      )}
    </span>
  )
}

export interface QuickFilterToggleProps {
  active: boolean
  onToggle: () => void
  /** Number of filters currently applied (shown as a count badge). */
  count?: number
  /** Compact chrome for tight hosts (smart-page embeds). */
  compact?: boolean
  /** Header-sized (36px) button — a dashboard screen's action row. */
  large?: boolean
}

/**
 * The mode switch. Docks into whatever action row the host already has (the
 * dashboard criteria bar's Apply row, an embed's header strip) — quick filter
 * is a MODE the user enters deliberately, so this is always visible and always
 * off until pressed.
 */
export function QuickFilterToggle({
  active, onToggle, count = 0, compact = false, large = false,
}: QuickFilterToggleProps) {
  const style: CSSProperties = large
    ? {
        display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0,
        height: 36, padding: '0 14px', borderRadius: 8,
        fontSize: 14, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
        border: `1px solid ${active ? ACCENT : 'var(--color-border, #e5e7eb)'}`,
        background: active ? 'var(--dash-weak, var(--color-accent-weak))' : 'var(--color-bg-secondary, #fff)',
        color: active ? ACCENT : 'var(--color-text, #111)',
      }
    : {
        display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
        fontSize: compact ? 11 : 12, fontWeight: 500, cursor: 'pointer',
        padding: compact ? '2px 7px' : '3px 9px', borderRadius: 6,
        border: `1px solid ${active ? ACCENT : 'var(--color-border, #e5e7eb)'}`,
        background: active ? ACCENT : 'var(--color-bg, #fff)',
        color: active ? '#fff' : 'var(--color-text-secondary, #6b7280)',
        whiteSpace: 'nowrap',
      }
  return (
    <button
      type="button"
      onClick={onToggle}
      style={style}
      aria-pressed={active}
      title={
        active
          ? 'Quick filter is ON — click any bar, slice, category or row to filter every visual by it. Click again to leave the mode.'
          : 'Quick filter: click a value in any visual to filter the whole dashboard by it'
      }
    >
      <FunnelIcon size={compact ? 11 : 13} />
      Quick filter{active ? ' · on' : ''}
      {count > 0 && (
        <span style={{
          fontSize: large ? 12 : 10, fontWeight: 700, lineHeight: large ? '20px' : 1.6,
          minWidth: large ? 20 : 16, textAlign: 'center',
          borderRadius: large ? 10 : 8, padding: large ? '0 6px' : '0 5px',
          background: large
            ? ACCENT
            : active ? 'rgba(255,255,255,0.25)' : 'var(--color-bg-secondary, #f3f4f6)',
          color: large || active ? '#fff' : 'var(--color-text, #111)',
        }}>
          {count}
        </span>
      )}
    </button>
  )
}

export interface QuickFilterBarProps {
  active: boolean
  chips: QuickFilterChip[]
  /** Remove one value from a chip (or the whole chip when value is omitted). */
  onRemove: (column: string, value?: unknown) => void
  onClearAll: () => void
  /** Leave the mode (the banner's own exit affordance). */
  onExit?: () => void
  /** How many tiles the current filters reached, out of how many filterable. */
  reach?: { filtered: number; total: number }
  /** Extra host chrome rendered at the end of the bar. */
  actions?: ReactNode
  compact?: boolean
}

/**
 * The mode banner + the applied chips. Renders nothing when the mode is off and
 * no filter is applied; a mode that is off but still carries chips keeps the
 * chips visible (the filters are still in force — leaving the mode stops new
 * clicks from filtering, it does not silently drop what you already applied).
 */
export function QuickFilterBar({
  active, chips, onRemove, onClearAll, onExit, reach, actions, compact = false,
}: QuickFilterBarProps) {
  if (!active && chips.length === 0) return null

  const pad = compact ? '4px 8px' : '6px 12px'
  return (
    <div
      data-quick-filter-bar
      style={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, padding: pad,
        borderBottom: '1px solid var(--color-border, #e5e7eb)',
        // Opaque in both states — the bar sits over the dashboard grid, and a
        // see-through band puts chart ink behind its own chip text.
        ...(active ? softAccent(0.10) : { backgroundColor: 'var(--color-bg-secondary, #f8f9fa)' }),
        borderLeft: active ? `3px solid ${ACCENT}` : '3px solid transparent',
        fontSize: compact ? 11 : 12, color: 'var(--color-text, #111)',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 600, color: ACCENT, flexShrink: 0 }}>
        <FunnelIcon size={compact ? 11 : 13} />
        Quick filter
      </span>

      {active && chips.length === 0 && (
        <span style={{ color: 'var(--color-text-secondary, #6b7280)' }}>
          Click a bar, slice, category or row in any visual to filter the whole
          dashboard by it. Hold {modifierKeyName()} to select more than one value.
        </span>
      )}

      {chips.map((chip) => (
        <span
          key={chip.column}
          title={
            (chip.sourceVisual ? `From ${chip.sourceVisual}. ` : '') +
            'Applied to every visual with a column of this name.'
          }
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%',
            padding: compact ? '1px 4px 1px 8px' : '2px 5px 2px 9px', borderRadius: 12,
            border: `1px solid ${ACCENT}`, background: 'var(--color-bg, #fff)',
            color: 'var(--color-text, #111)', whiteSpace: 'nowrap',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{chipCaption(chip)}</span>
          <button
            type="button"
            onClick={() => onRemove(chip.column)}
            aria-label={`Remove filter ${chip.label}`}
            title={`Remove filter on ${chip.label}`}
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer', lineHeight: 1,
              padding: 0, fontSize: compact ? 11 : 12, color: 'var(--color-text-secondary, #6b7280)',
            }}
          >
            ✕
          </button>
        </span>
      ))}

      {chips.length > 0 && (
        <>
          <button
            type="button"
            onClick={onClearAll}
            style={{
              border: 'none', background: 'transparent', cursor: 'pointer', padding: 0,
              fontSize: compact ? 11 : 12, color: ACCENT, textDecoration: 'underline',
              flexShrink: 0,
            }}
          >
            Clear all
          </button>
          {reach && (
            <span style={{ color: 'var(--color-text-secondary, #6b7280)', flexShrink: 0 }}>
              {reach.filtered} of {reach.total} visuals filtered
              {/* Each unreached tile has its OWN reason (no such column, raw SQL,
                  a report tile, a truncated dataset), so point at the badge that
                  states it rather than asserting one reason for all of them. */}
              {reach.filtered < reach.total ? ' — hover a tile’s badge for why' : ''}
            </span>
          )}
        </>
      )}

      <span style={{ flex: 1 }} />
      {actions}
      {active && onExit && (
        <button
          type="button"
          onClick={onExit}
          title="Leave quick filter mode (Esc). Applied filters stay until you clear them."
          style={{
            border: `1px solid ${ACCENT}`, background: 'transparent', cursor: 'pointer',
            borderRadius: 6, padding: compact ? '1px 6px' : '2px 8px',
            fontSize: compact ? 10 : 11, color: ACCENT, flexShrink: 0, whiteSpace: 'nowrap',
          }}
        >
          Done
        </button>
      )}
    </div>
  )
}

export default QuickFilterBar
